import { useEffect, useRef } from "react";
import { CustomModal, Typography } from "get-life-storybook-ts";
import { ButtonsModalI } from "get-life-storybook-ts/lib/organisms/Modal/CustomModal";
import { customWeCallYouStyles } from "../../constants/CustomStyles/WeCallYou";
import numberFormat from "../../utils/Utils";

interface DowngradedTraitsModalProps {
  isOpen: boolean;
  removedTraits: string[];
  capitalsIfApplied: Record<string, number>;
  translate: (key: string) => string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ModalPortalHandle {
  handleShowPortal: () => void;
  handleClosePortal: () => void;
}

const DowngradedTraitsModal = ({
  isOpen,
  removedTraits,
  capitalsIfApplied,
  translate,
  onConfirm,
  onCancel,
}: DowngradedTraitsModalProps) => {
  const modalRef = useRef<ModalPortalHandle | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      modalRef.current?.handleShowPortal();
      wasOpenRef.current = true;
    } else if (!isOpen && wasOpenRef.current) {
      modalRef.current?.handleClosePortal();
      wasOpenRef.current = false;
    }
  }, [isOpen]);

  const translatedTraits = removedTraits
    .map((trait) => translate(`trait.${trait}`) || trait)
    .join(", ");

  const message = (translate("questions.downgraded.modal.message") || "").replace(
    "{{traits}}",
    translatedTraits
  );

  const ModalTitle = (
    <Typography
      variant={customWeCallYouStyles.titleVariant}
      weight={customWeCallYouStyles.titleWeight}
      text={translate("questions.downgraded.modal.title")}
    />
  );

  const remainingCapitals = Object.entries(capitalsIfApplied);

  const ModalBody = (
    <>
      <Typography
        variant={customWeCallYouStyles.bodyTextVariant}
        weight={customWeCallYouStyles.bodyTextWeight}
        text={message}
      />
      {remainingCapitals.length > 0 && (
        <>
          <Typography
            variant={customWeCallYouStyles.bodyTextVariant}
            weight={customWeCallYouStyles.bodyTextWeight}
            text={translate("questions.downgraded.modal.newCapitals")}
          />
          <ul>
            {remainingCapitals.map(([trait, capital]) => (
              <li key={trait}>
                <Typography
                  variant={customWeCallYouStyles.bodyTextVariant}
                  weight={customWeCallYouStyles.bodyTextWeight}
                  text={`${translate(`trait.${trait}`) || trait}: ${numberFormat(capital, "capital")}`}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );

  const handleAccept = () => {
    modalRef.current?.handleClosePortal();
    wasOpenRef.current = false;
    onConfirm();
  };

  const handleCancel = () => {
    modalRef.current?.handleClosePortal();
    wasOpenRef.current = false;
    onCancel();
  };

  const ModalActions: ButtonsModalI = {
    showBoth: true,
    preventClose: true,
    buttonInfo: {
      primary: {
        label: translate("questions.downgraded.modal.accept"),
        onClick: handleAccept,
      },
      secondary: {
        label: translate("questions.downgraded.modal.reject"),
        onClick: handleCancel,
      },
    },
  };

  return (
    <CustomModal
      ref={modalRef}
      ModalTitle={ModalTitle}
      ModalBody={ModalBody}
      ModalActions={ModalActions}
      closeModalCallback={() => {
        wasOpenRef.current = false;
        onCancel();
      }}
    />
  );
};

export default DowngradedTraitsModal;
